// ============================================================================
// ConcurrencyManager
// ------------------
// Limits how many requests a given axios instance can run concurrently, by
// queuing outgoing requests through interceptors and releasing them as
// running requests complete.
//
// Inlined from the (now archived/unmaintained) 'axios-concurrency' package:
// https://github.com/bernawil/axios-concurrency - reproduced here to drop
// the dependency, which pulls in its own pinned, vulnerable axios@0.21.x
// copy that was never actually used by its code (axios is only received as
// a parameter, never required internally).
// ============================================================================

export const ConcurrencyManager = (axios, maxConcurrent = 100) => {
	if (maxConcurrent < 1) {
		throw new Error('Concurrency Manager Error: minimum concurrent requests is 1')
	}

	const instance = {
		queue: [],
		running: [],
		shiftInitial: () => {
			setTimeout(() => {
				if (instance.running.length < maxConcurrent) {
					instance.shift()
				}
			}, 0)
		},
		push: reqHandler => {
			instance.queue.push(reqHandler)
			instance.shiftInitial()
		},
		shift: () => {
			if (instance.queue.length) {
				const queued = instance.queue.shift()
				queued.resolver(queued.request)
				instance.running.push(queued)
			}
		},
		// Use as interceptor. Queue outgoing requests
		requestHandler: req => {
			return new Promise(resolve => {
				instance.push({ request: req, resolver: resolve })
			})
		},
		// Use as interceptor. Execute queued request upon receiving a response
		responseHandler: res => {
			instance.running.shift()
			instance.shift()
			return res
		},
		responseErrorHandler: res => {
			return Promise.reject(instance.responseHandler(res))
		},
		interceptors: {
			request: null,
			response: null
		},
		detach: () => {
			axios.interceptors.request.eject(instance.interceptors.request)
			axios.interceptors.response.eject(instance.interceptors.response)
		}
	}

	// queue concurrent requests
	instance.interceptors.request = axios.interceptors.request.use(instance.requestHandler)
	instance.interceptors.response = axios.interceptors.response.use(instance.responseHandler, instance.responseErrorHandler)

	return instance
}
